/** Manages and persists task filtering state, including hidden projects and contexts, using localStorage. */
import { useState, useEffect } from 'react';
import { FilterContext } from '@/features/tasks/hooks/list/use-list-filters.js';

/**
 * Reads a JSON array from localStorage by key.
 *
 * localStorage format: JSON-serialised string array, e.g.
 *   key "hiddenProjects"  → '["work","personal"]'
 *   key "hiddenContexts"  → '["home"]'
 *
 * Fallback behaviour:
 *   - Key absent              → returns []
 *   - Invalid JSON            → logs error, returns []
 *   - Parsed value not array  → returns [] (guards against type-mismatches
 *     such as a stored string or object from a previous schema version)
 *
 * @param {string} key - localStorage key to read.
 * @returns {string[]} The stored array, or [] on any failure.
 */
function readArrayFromStorage(key) {
  try {
    const saved = localStorage.getItem(key);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error(`Failed to parse "${key}" from localStorage`, error);
    return [];
  }
}

export const FilterProvider = ({ children }) => {
  const [hiddenProjects, setHiddenProjects] = useState(() =>
    readArrayFromStorage('hiddenProjects'),
  );

  const [hiddenContexts, setHiddenContexts] = useState(() =>
    readArrayFromStorage('hiddenContexts'),
  );

  useEffect(() => {
    localStorage.setItem('hiddenProjects', JSON.stringify(hiddenProjects));
  }, [hiddenProjects]);

  useEffect(() => {
    localStorage.setItem('hiddenContexts', JSON.stringify(hiddenContexts));
  }, [hiddenContexts]);

  const toggleProjectVisibility = (project) => {
    setHiddenProjects((prev) =>
      prev.includes(project)
        ? prev.filter((p) => p !== project)
        : [...prev, project],
    );
  };

  const toggleContextVisibility = (context) => {
    setHiddenContexts((prev) =>
      prev.includes(context)
        ? prev.filter((c) => c !== context)
        : [...prev, context],
    );
  };

  /**
   * Removes stale entries from hiddenProjects/hiddenContexts that are no longer
   * present in the active display maps. Call this whenever the active maps change.
   *
   * @param {Set<string>} activeProjectIds - Set of currently active project identifiers.
   * @param {Set<string>} activeContextIds - Set of currently active context identifiers.
   */
  const cleanupHidden = (activeProjectIds, activeContextIds) => {
    setHiddenProjects((prev) => prev.filter((p) => activeProjectIds.has(p)));
    setHiddenContexts((prev) => prev.filter((c) => activeContextIds.has(c)));
  };

  const value = {
    hiddenProjects,
    setHiddenProjects,
    toggleProjectVisibility,
    hiddenContexts,
    setHiddenContexts,
    toggleContextVisibility,
    cleanupHidden,
  };

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};
